import React, { useEffect, useRef, useState } from "react";
import ClassicLEDDisplay from "./ClassicLEDDisplay";
import ModernLCDDisplay from "./ModernLCDDisplay";
import RailcarLEDDisplay from "./RailcarLEDDisplay";
import PlatformIndicatorDisplay from "./PlatformIndicatorDisplay";
import { Button } from "@/components/ui/button";
import { Maximize2, X, RotateCcw, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

const MODES = [
  { key: "modern", label: "Modern LCD" },
  { key: "classic", label: "Classic LED" },
  { key: "railcar", label: "Railcar" },
  { key: "platform", label: "Platform" },
];

export default function DisplayScreen({ mode, onSetMode, ...data }) {
  const containerRef = useRef(null);
  const [kiosk, setKiosk] = useState(false);
  const [orientationHint, setOrientationHint] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Export the live display as a PNG
  const exportPng = async () => {
    const el = containerRef.current;
    if (!el) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: 3, // crisp for retina + social sharing
        cacheBust: true,
        backgroundColor: "#000000",
        // Some display components use webfonts loaded from Google Fonts; skip
        // any external stylesheet fetches that would fail CORS at export time.
        filter: (node) => {
          if (node.tagName === "LINK" && node.href && !node.href.startsWith(window.location.origin)) {
            return false;
          }
          return true;
        },
      });
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `syd-pid-${mode}-${stamp}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("PNG downloaded");
    } catch (err) {
      toast.error("Couldn't export PNG");
    } finally {
      setExporting(false);
    }
  };

  // Native + CSS-fallback fullscreen
  const enterFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;

    // Try native Fullscreen API first (works on Android, iPad, desktop)
    const supportsNative =
      el.requestFullscreen || el.webkitRequestFullscreen || el.webkitEnterFullscreen;
    if (supportsNative) {
      try {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          await (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        } else if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        } else if (el.webkitEnterFullscreen) {
          el.webkitEnterFullscreen();
        }
        // Try to lock orientation landscape for a more TV-board feel
        try {
          if (screen.orientation && screen.orientation.lock) {
            await screen.orientation.lock("landscape").catch(() => {});
          }
        } catch { /* ignore */ }
        return;
      } catch {
        /* fall through to CSS kiosk */
      }
    }

    // CSS kiosk fallback (iPhone Safari + anything else without FS API)
    setKiosk(true);
  };

  const exitKiosk = async () => {
    setKiosk(false);
    try {
      if (screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
      }
    } catch { /* ignore */ }
  };

  // Sync kiosk with native fullscreen exits (ESC key, swipe-down)
  useEffect(() => {
    const onFsChange = () => {
      const fs = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fs) {
        // Native FS exited — kiosk fallback stays handled separately
        try { screen.orientation && screen.orientation.unlock && screen.orientation.unlock(); } catch { /* ignore */ }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // While in CSS kiosk, listen for ESC and lock body scroll
  useEffect(() => {
    if (!kiosk) return;
    const onKey = (e) => { if (e.key === "Escape") exitKiosk(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Orientation: if portrait phone, suggest rotating
    const checkOrientation = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      const isPhone = window.innerWidth < 768;
      setOrientationHint(isPortrait && isPhone);
    };
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    // Try to lock landscape; ignore failures (iOS rejects on PWA-less context)
    try {
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation.lock("landscape").catch(() => {});
      }
    } catch { /* ignore */ }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, [kiosk]);

  const aspect = mode === "railcar"
    ? "aspect-[24/9] sm:aspect-[32/9]"
    : mode === "platform"
      ? "aspect-[3/4] sm:aspect-[9/16]"
      : "aspect-[16/9] sm:aspect-[21/9]";

  const displayBody = (
    <>
      {mode === "classic" && <ClassicLEDDisplay {...data} />}
      {mode === "modern" && <ModernLCDDisplay {...data} />}
      {mode === "railcar" && <RailcarLEDDisplay {...data} />}
      {mode === "platform" && <PlatformIndicatorDisplay {...data} />}
    </>
  );

  return (
    <div className="w-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 gap-2 sm:gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-mono uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[10px] sm:text-xs text-white/60">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 pulse-dot" />
          <span data-testid="display-toolbar-label">Live preview</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto">
          <div
            className="inline-flex border border-white/20"
            data-testid="mode-selector"
            role="tablist"
          >
            {MODES.map((m) => {
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onSetMode(m.key)}
                  data-testid={`mode-${m.key}-button`}
                  className={`px-2.5 sm:px-3 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-[11px] transition-colors ${
                    active
                      ? "bg-white text-black"
                      : "bg-transparent text-white/80 hover:bg-white/10"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={exportPng}
            disabled={exporting}
            data-testid="export-png-button"
            className="rounded-none border-[#FFB000]/40 bg-transparent text-[#FFB000] hover:bg-[#FFB000] hover:text-black uppercase font-mono tracking-widest text-[10px] sm:text-[11px] h-9"
          >
            <Download className={`w-3.5 h-3.5 sm:mr-2 ${exporting ? "animate-pulse" : ""}`} />
            <span className="hidden sm:inline">{exporting ? "…" : "PNG"}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={enterFullscreen}
            data-testid="fullscreen-button"
            className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black uppercase font-mono tracking-widest text-[10px] sm:text-[11px] h-9"
          >
            <Maximize2 className="w-3.5 h-3.5 sm:mr-2" />
            <span className="hidden sm:inline">Fullscreen</span>
          </Button>
        </div>
      </div>

      {/* Inline display surface */}
      <div
        ref={containerRef}
        data-testid="live-display-container"
        className={`relative w-full ${aspect} bg-black border border-white/15 overflow-hidden`}
      >
        {/* Hide inline body while CSS kiosk is active so the portal owns the screen */}
        {!kiosk && displayBody}
      </div>

      {/* Helper text */}
      <p className="mt-3 text-[11px] text-white/40 font-mono uppercase tracking-[0.25em]">
        Press <kbd className="px-1.5 py-0.5 border border-white/20 text-white/80">ESC</kbd> to exit fullscreen
      </p>

      {/* CSS Kiosk overlay (iOS Safari + anywhere FS API is blocked) */}
      {kiosk && (
        <div
          className="fixed inset-0 z-[1000] bg-black flex items-center justify-center"
          data-testid="kiosk-overlay"
          style={{
            // Respect notches / status bars
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
          }}
        >
          {/* Display fills the viewport, scaled to its natural aspect */}
          <div
            className="relative w-full h-full"
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
            }}
          >
            {displayBody}
          </div>

          {/* Exit button — top-right, large touch target */}
          <button
            type="button"
            onClick={exitKiosk}
            data-testid="kiosk-exit-button"
            aria-label="Exit fullscreen"
            className="absolute top-3 right-3 z-[1001] w-11 h-11 flex items-center justify-center bg-black/60 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-black transition-colors"
            style={{
              top: "calc(env(safe-area-inset-top) + 12px)",
              right: "calc(env(safe-area-inset-right) + 12px)",
            }}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Portrait phone hint */}
          {orientationHint && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1001] flex items-center gap-2 px-3 py-2 bg-black/70 backdrop-blur border border-white/20 text-white font-mono uppercase tracking-widest text-[10px]"
              data-testid="kiosk-orientation-hint"
              style={{ bottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Rotate phone for full effect
            </div>
          )}
        </div>
      )}
    </div>
  );
}
